import type { LucideIcon } from 'lucide-react';
import { AlertCircle, Clock, Loader2 } from 'lucide-react';
import { Outlet, useParams, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useTenant } from '@/context';
import { useCase } from '@/hooks/use-cases';

/**
 * Determine the blocking state from a case's jobs array.
 *
 * The guard's purpose is to block access until the *initial* processing job
 * has completed.  Once any job has reached "completed", the case data is
 * available and the viewer should stay accessible — even if subsequent
 * workflow-internal jobs (e.g. dosimetry.compute) are queued or running.
 *
 * When no job has completed yet, we fall back to the latest job's status so
 * the user sees the appropriate "queued / processing / failed" message.
 */
function getBlockingJob(
    jobs: Array<{ status: string; error?: string | null; createdAt: string }> | undefined
) {
    if (!jobs || jobs.length === 0) {
        return null;
    }

    const hasCompleted = jobs.some((j) => j.status === 'completed');
    if (hasCompleted) {
        // At least one job completed — viewer should be accessible.
        return { status: 'completed' } as const;
    }

    // No completed job yet — return the latest to show its status.
    const sorted = [...jobs].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return sorted[0];
}

/**
 * Configuration for a blocking state.
 */
interface BlockingConfig {
    icon: LucideIcon;
    iconClassName: string;
    title: string;
    description: string;
    error?: string | null;
}

/**
 * Blocking card shown when the case cannot be viewed.
 */
function BlockingCard({ config, onGoBack }: { config: BlockingConfig; onGoBack: () => void }) {
    const Icon = config.icon;

    return (
        <div className="container mx-auto max-w-lg py-12">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${config.iconClassName}`} />
                        <CardTitle>{config.title}</CardTitle>
                    </div>
                    <CardDescription>{config.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {config.error && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                            {config.error}
                        </div>
                    )}
                    <Button onClick={onGoBack}>Back to Cases</Button>
                </CardContent>
            </Card>
        </div>
    );
}

/**
 * Get blocking configuration based on job status.
 */
function getBlockingConfig(
    latestJob: { status: string; error?: string | null } | null
): BlockingConfig | null {
    if (!latestJob) {
        return {
            icon: AlertCircle,
            iconClassName: 'text-yellow-500',
            title: 'No Processing Jobs',
            description: 'This case has not been processed yet. Please start a processing job first.',
        };
    }

    switch (latestJob.status) {
        case 'queued':
            return {
                icon: Clock,
                iconClassName: 'text-yellow-500',
                title: 'Processing Queued',
                description: 'This case is waiting in the processing queue. Please check back later.',
            };
        case 'processing':
            return {
                icon: Loader2,
                iconClassName: 'animate-spin text-blue-500',
                title: 'Processing in Progress',
                description:
                    'This case is currently being processed. Please check back when processing is complete.',
            };
        case 'failed':
            return {
                icon: AlertCircle,
                iconClassName: 'text-destructive',
                title: 'Processing Failed',
                description:
                    'The processing job for this case failed. Please try reprocessing or contact support.',
                error: latestJob.error,
            };
        case 'completed':
            return null; // No blocking
        default:
            return {
                icon: AlertCircle,
                iconClassName: 'text-yellow-500',
                title: 'Unknown Status',
                description: `The case has an unexpected job status: ${latestJob.status}`,
            };
    }
}

/**
 * Route guard that ensures a case has completed processing before allowing access.
 */
export function ProcessingCompleteGuard() {
    const { caseId } = useParams<{ caseId: string }>();
    const { tenantId } = useTenant();
    const navigate = useNavigate();
    const { data: caseData, isLoading, error } = useCase(caseId);

    const handleGoBack = () => {
        navigate(`/tenants/${tenantId}`);
    };

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Spinner className="h-8 w-8" />
                    <p className="text-muted-foreground">Loading case information...</p>
                </div>
            </div>
        );
    }

    if (error || !caseData) {
        return (
            <BlockingCard
                config={{
                    icon: AlertCircle,
                    iconClassName: 'text-destructive',
                    title: 'Unable to Load Case',
                    description: error instanceof Error ? error.message : 'Failed to load case information',
                }}
                onGoBack={handleGoBack}
            />
        );
    }

    const blockingJob = getBlockingJob(caseData.jobs);
    const blockingConfig = getBlockingConfig(blockingJob);

    if (blockingConfig) {
        return <BlockingCard config={blockingConfig} onGoBack={handleGoBack} />;
    }

    return <Outlet />;
}
